'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Camera, FileText, Send, AlertCircle, Loader2, Phone } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';

export function ComplaintForm() {
  const router = useRouter();
  const { t } = useLanguage();
  const [step, setStep] = useState<'check-phone' | 'not-registered' | 'form'>('check-phone');
  const [phone, setPhone] = useState('');
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [userData, setUserData] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<any>(null);

  const handlePhoneCheck = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckingPhone(true);
    setError(null);

    try {
      const res = await fetch(`/api/users/check-phone?phone=${encodeURIComponent(phone)}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to verify phone number');
      }

      if (data.registered) {
        setUserData(data.user);
        setStep('form');
      } else {
        setStep('not-registered');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCheckingPhone(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);

    try {
      const res = await fetch('/api/complaints', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit complaint');
      }

      setSuccess(data.complaint);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-8 text-center max-w-2xl mx-auto">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Send className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {t('complaintSuccess')}
        </h2>
        <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('complaintNo')}</p>
              <p className="text-xl font-bold" style={{ color: '#E67E22' }}>{success.complaint_no}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('date')}</p>
              <p className="text-lg font-medium">{new Date(success.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('currentStatus')}</p>
              <p className="text-lg font-medium text-blue-600">{success.status}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => router.push('/track-complaint')}
          className="text-white px-8 py-3 rounded-xl font-bold text-lg transition-colors"
          style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}
        >
          {t('trackComplaintBtn')}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-4xl mx-auto">
      <div className="px-8 py-6 text-white text-center" style={{ background: 'linear-gradient(135deg, #4A2E14, #E67E22)' }}>
        <h2 className="text-2xl font-bold mb-2">{t('registerGrievance')}</h2>
        <p className="text-white/80">{t('grievanceSubtitle')}</p>
      </div>

      {step === 'check-phone' && (
        <form onSubmit={handlePhoneCheck} className="p-8 space-y-6 max-w-md mx-auto">
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-start gap-3 border border-red-100">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('verifyPhone')}</h3>
            <p className="text-gray-500 text-sm">{t('verifyPhoneSubtitle')}</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t('mobileNo')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                pattern="[0-9]{10}"
                placeholder={t('tenDigit')}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#E67E22] focus:ring-1 focus:ring-[#E67E22] outline-none transition-all"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={checkingPhone || !phone.match(/^[0-9]{10}$/)}
            className="w-full text-white px-8 py-3 rounded-xl font-bold transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}
          >
            {checkingPhone ? <Loader2 className="w-5 h-5 animate-spin" /> : t('continue')}
          </button>
        </form>
      )}

      {step === 'not-registered' && (
        <div className="p-8 text-center max-w-md mx-auto space-y-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('numberNotRegistered')}</h3>
            <p className="text-gray-600">{t('numberNotRegisteredMsg')}</p>
          </div>
          <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => router.push('/registration')}
              className="text-white px-6 py-2.5 rounded-xl font-bold transition-colors"
              style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}
            >
              {t('goToRegistration')}
            </button>
            <button
              onClick={() => {
                setStep('check-phone');
                setPhone('');
              }}
              className="bg-gray-100 text-gray-700 px-6 py-2.5 rounded-xl font-bold hover:bg-gray-200 transition-colors"
            >
              {t('tryAnotherNumber')}
            </button>
          </div>
        </div>
      )}

      {step === 'form' && userData && (
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-start gap-3 border border-red-100">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Citizen Details */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4A017]" />
              {t('citizenDetails')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('firstName')} <span className="text-red-500">*</span></label>
                <input name="first_name" defaultValue={userData.first_name} required readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('middleName')}</label>
                <input name="middle_name" defaultValue={userData.middle_name} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('lastName')} <span className="text-red-500">*</span></label>
                <input name="last_name" defaultValue={userData.last_name} required readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('mobileNumber')} <span className="text-red-500">*</span></label>
                <input name="mobile_no" defaultValue={userData.mobile_no} required readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('whatsappNumber')}</label>
                <input name="whatsapp_no" defaultValue={userData.whatsapp_no} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('emailAddress')}</label>
                <input name="email" type="email" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#E67E22] focus:ring-1 focus:ring-[#E67E22] outline-none transition-all" />
              </div>

              <div className="space-y-2 md:col-span-3">
                <label className="text-sm font-medium text-gray-700">{t('fullAddressLabel')}</label>
                <textarea name="full_address" rows={2} defaultValue={userData.full_address} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all"></textarea>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('state')}</label>
                <input name="state" defaultValue={userData.state} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed text-gray-600 outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('district')}</label>
                <input name="district" defaultValue={userData.district} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed text-gray-600 outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('wardNo')}</label>
                <input name="ward_number" defaultValue={userData.ward_number} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('pincode')}</label>
                <input name="pincode" defaultValue={userData.pincode} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('gender')}</label>
                <input name="gender" defaultValue={userData.gender} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('bloodGroup')}</label>
                <input name="blood_group" defaultValue={userData.blood_group} readOnly className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 cursor-not-allowed outline-none transition-all" />
              </div>
            </div>
          </div>

          {/* Complaint Details */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#D4A017]" />
              {t('complaintInfo')}
            </h3>
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('complaintTitle')} <span className="text-red-500">*</span></label>
                <input name="title" required maxLength={150} placeholder={t('complaintTitlePlaceholder')} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#E67E22] focus:ring-1 focus:ring-[#E67E22] outline-none transition-all" />
                <p className="text-xs text-gray-500">{t('maxChars')}</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('complaintDescription')} <span className="text-red-500">*</span></label>
                <textarea name="description" required minLength={30} maxLength={3000} rows={5} placeholder={t('descPlaceholder')} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#E67E22] focus:ring-1 focus:ring-[#E67E22] outline-none transition-all"></textarea>
                <p className="text-xs text-gray-500">{t('minMaxChars')}</p>
              </div>



              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('docUpload')}</label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#E67E22] transition-colors relative">
                  <input
                    type="file"
                    name="documents"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-medium">{t('clickOrDrag')}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('supportedFormats')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="text-white px-8 py-3 rounded-xl font-bold transition-colors disabled:opacity-70 flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              {t('submitComplaint')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
